import { Injectable, OnDestroy } from '@angular/core';
import { Subscription, timer } from 'rxjs';

import { FuseNavigationService } from '../../../@fuse/components/navigation/navigation.service';
import { SubmissionsService } from '../api/submissions.service';
import { ModulesService } from './modules.service';
import { SessionEventsService } from '../api/session-events.service';

/**
 * Puce « Soumissions » du menu principal = nombre de soumissions web en attente
 * (GET /v1/submissions/pending-count). Même mécanique que la puce du chat GOD
 * (FuseNavigationService.updateNavigationItem). Rafraîchie au chargement (une
 * fois les modules connus), après chaque action sur les soumissions
 * (refresh()) et toutes les 10 minutes.
 */
@Injectable({ providedIn: 'root' })
export class SubmissionsBadgeService implements OnDestroy {
  private static readonly NAV_ITEM_ID = 'submissions';
  private static readonly POLL_MS = 10 * 60 * 1000;

  private pollSub?: Subscription;
  private started = false;

  constructor(
    private submissionsService: SubmissionsService,
    private modulesService: ModulesService,
    private fuseNavigationService: FuseNavigationService,
    private sessionEvents: SessionEventsService,
  ) {
    this.sessionEvents.logout$.subscribe(() => this.stop());
  }

  /** Call once the org modules are known (app.component, modulesSet). Idempotent. */
  public start(): void {
    if (this.started || !this.modulesService.hasSubmissions()) {
      return;
    }
    this.started = true;
    this.pollSub = timer(0, SubmissionsBadgeService.POLL_MS).subscribe(() =>
      this.refresh(),
    );
  }

  public stop(): void {
    this.pollSub?.unsubscribe();
    this.pollSub = undefined;
    this.started = false;
    this.applyBadge(0);
  }

  /** Re-read the pending count (after convert / archive / unarchive / delete). */
  public refresh(): void {
    if (!this.modulesService.hasSubmissions()) {
      return;
    }
    this.submissionsService.getPendingCount().subscribe({
      next: (response) => this.applyBadge(response?.data?.count ?? 0),
      error: () => {
        /* silent: the badge is a convenience, never an error source */
      },
    });
  }

  private applyBadge(count: number): void {
    // _.merge never removes a key: clearing requires an explicit null (see chat badge).
    this.fuseNavigationService.updateNavigationItem(
      SubmissionsBadgeService.NAV_ITEM_ID,
      {
        badge:
          count > 0
            ? { title: String(count), bg: '#F44336', fg: '#FFFFFF' }
            : (null as unknown as undefined),
      },
    );
  }

  ngOnDestroy(): void {
    this.stop();
  }
}
